
import React from "react";

export const WhyT12Matters = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-teal-50 border border-teal-200 rounded-full mb-6">
            <span className="text-teal-600 text-sm font-semibold">Critical Importance</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Why T12 Matters When Selling Your Apartment Building
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Your T12 statement directly impacts your property's valuation, buyer confidence, and final sale price. Here's why it's the most scrutinized document in any transaction.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div className="relative">
            <div className="aspect-w-4 aspect-h-3 rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/73266242df746e2ceedc271f073f366b.jpg"
                alt="Real estate investor analyzing T12 statements for property valuation"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="space-y-6">
            {/* Determines Property Value */}
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-money-dollar-circle-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Determines Property Value</h3>
                  <p className="text-gray-700 mb-3">
                    Buyers calculate your property's value using the income capitalization approach:{" "}
                    <strong>Value = T12 NOI ÷ Cap Rate</strong>. Your T12 NOI is the numerator in this equation,
                    directly determining your sale price.
                  </p>
                  <div className="bg-teal-50 rounded-lg p-4">
                    <p className="text-sm text-gray-700">
                      <strong>Example:</strong> T12 NOI of $500,000 ÷ 6% cap rate = $8,333,333 property value.
                      Increase NOI to $550,000 = $9,166,667 value (+$833,334)
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Required for Financing */}
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-bank-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Required for Financing</h3>
                  <p className="text-gray-700">
                    Lenders require T12 to underwrite loans for buyers. Without clean, accurate T12 documentation,
                    buyers cannot secure financing, which eliminates 70‑80% of potential buyers from your pool.
                  </p>
                </div>
              </div>
            </div>

            {/* Builds Buyer Confidence */}
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Builds Buyer Confidence</h3>
                  <p className="text-gray-700">
                    Strong T12 performance with consistent income, controlled expenses, and high occupancy rates
                    gives buyers confidence in the property's stability and reduces their perceived risk.
                  </p>
                </div>
              </div>
            </div>

            {/* Due Diligence Foundation */}
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-search-eye-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Due Diligence Foundation</h3>
                  <p className="text-gray-700">
                    T12 is the starting point for all buyer due diligence. They'll verify every line item against
                    bank statements, rent rolls, invoices, and tax returns. Inconsistencies kill deals.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Impact of T12 on Sale Price */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Impact of T12 on Sale Price
          </h3>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-4 px-4 font-bold text-gray-900">T12 Quality</th>
                  <th className="text-left py-4 px-4 font-bold text-gray-900">Buyer Response</th>
                  <th className="text-left py-4 px-4 font-bold text-gray-900">Price Impact</th>
                  <th className="text-left py-4 px-4 font-bold text-gray-900">Financing</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {/* Strong T12 */}
                <tr>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <span className="font-semibold text-gray-900">Strong T12</span>
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      Consistent income, low expenses, high occupancy
                    </div>
                  </td>
                  <td className="py-4 px-4 text-gray-700">Multiple offers, competitive bidding</td>
                  <td className="py-4 px-4">
                    <span className="text-green-600 font-bold">+10% to +25%</span>
                    <div className="text-sm text-gray-600">Above market value</div>
                  </td>
                  <td className="py-4 px-4 text-gray-700">Easy approval, favorable terms</td>
                </tr>

                {/* Average T12 */}
                <tr>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                      <span className="font-semibold text-gray-900">Average T12</span>
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      Stable but unremarkable performance
                    </div>
                  </td>
                  <td className="py-4 px-4 text-gray-700">Standard interest, normal process</td>
                  <td className="py-4 px-4">
                    <span className="text-gray-900 font-bold">Market Value</span>
                    <div className="text-sm text-gray-600">Fair market price</div>
                  </td>
                  <td className="py-4 px-4 text-gray-700">Standard approval process</td>
                </tr>

                {/* Weak T12 */}
                <tr>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <span className="font-semibold text-gray-900">Weak T12</span>
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      Declining income, high expenses, vacancies
                    </div>
                  </td>
                  <td className="py-4 px-4 text-gray-700">Heavy scrutiny, low offers, deal risk</td>
                  <td className="py-4 px-4">
                    <span className="text-red-600 font-bold">-15% to -40%</span>
                    <div className="text-sm text-gray-600">Below market value</div>
                  </td>
                  <td className="py-4 px-4 text-gray-700">Difficult or impossible</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};
